import { makeObservable, observable, action, runInAction, reaction, computed } from 'mobx';
import { getAgentsByIds, searchAgents, type AgentSearchParams } from '@/api/agents';
import { getMyPinnedAgents, pinAgent, unpinAgent } from '@/api/userAgentPins';
import type { AgentDefinition } from '@/api/types';
import type { AgentStatusFilter } from '@/components/agentStatus';
import type { RootStore } from './RootStore';

// Recent-agents bucket lives in localStorage — instant, no DB round-trip,
// per-browser. Cap at 10 entries; oldest evicted on overflow. Keyed by
// merchant id so switching merchants doesn't bleed history across tenants.
const RECENT_MAX = 10;
const RECENT_KEY_PREFIX = 'agentstudio.recent_agents.';
const recentKey = (merchantId: number) => `${RECENT_KEY_PREFIX}${merchantId}`;

function readRecent(merchantId: number): number[] {
  try {
    const raw = window.localStorage.getItem(recentKey(merchantId));
    if (!raw) return [];
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return [];
    return arr.filter((x): x is number => typeof x === 'number');
  } catch {
    return [];
  }
}
function writeRecent(merchantId: number, ids: number[]): void {
  try {
    window.localStorage.setItem(recentKey(merchantId), JSON.stringify(ids.slice(0, RECENT_MAX)));
  } catch {
    /* localStorage full / disabled — silently degrade */
  }
}

// Filter values for the agent list drawer.
//
// Simplified per operator feedback ("the 7-option matrix was confusing").
// Operators only think about audience in 3 buckets:
//   - All       → no constraint
//   - Patient   → agent.patientApp === true (regardless of careTeam)
//   - Care team → agent.merchantApp === true (regardless of patient)
//
// The "Patient only / Care team only / Both / Neither" combinations
// were never used in practice; collapsing keeps the filter scannable.
export type AudienceFilter = 'all' | 'patient' | 'careTeam';
/** The lifecycle toggle (components/agentStatus): all, active, draft, inactive, archived. */
export type StatusFilter = AgentStatusFilter;
export type RouterFilter = 'all' | 'enabled' | 'disabled';
/**
 * Category filter — `'all'` = no filter, otherwise a numeric ai.AiAgentCategory.Id.
 * The list of available categories is loaded by AgentFilterDrawer at open time
 * (one /api/AiAgentCategory/Search round-trip). The store doesn't cache them
 * because the lookup table is small (~20 rows) and stale data would surface
 * very visibly — better to refetch.
 */
export type CategoryFilter = 'all' | number;

/** A column the list can be ordered by on the server; null is the practice's display order. */
export type AgentListSort = { field: 'name' | 'category'; descending: boolean } | null;

export const AGENT_LIST_PAGE_SIZES = [10, 25, 50, 100];

// THE AGENTS PAGE'S STORE (2 Nov 2026): the ONE page of the practice's agents the list shows, as the SERVER searched,
// filtered, ordered and paged them (api/Agent/Search → AIAgentConfigManager.SearchDirectoryAsync). It used to hold the
// first 100 agents and filter them in the browser, and several screens looked agents up in it; a practice has more
// (256 at practice 100), so agents past the first page could not be found, filtered, linked or named. It is not a
// roster: anything else that needs agents asks the directory (useAgentDirectory, useAgentRow, getAgentsByIds), and a
// source-scan test keeps the list of files that may read this store short.
// Observes MerchantContextStore.selectedMerchantId and refetches when the practice changes.

export class AgentRegistryStore {
  /** The page the Agents list shows — never a roster to look an agent up in. */
  @observable agents: AgentDefinition[] = [];
  /** The server's count for the current search and filters. */
  @observable totalCount = 0;
  @observable isLoading = false;
  @observable error: string | null = null;
  @observable search = '';
  /** The text the shown page was asked with ('' for none). */
  @observable shownSearch = '';
  @observable includeGlobal = false;
  @observable page = 1;
  @observable pageSize = 10;
  @observable sort: AgentListSort = null;
  // Filter state, sent to the server with every request.
  @observable audienceFilter: AudienceFilter = 'all';
  @observable statusFilter: StatusFilter = 'all';
  @observable routerFilter: RouterFilter = 'all';
  // Category filter is the only numeric-id filter so far. 'all' means
  // no category constraint; a number value matches AgentDefinition.categoryId
  // exactly. We don't filter by category-name string because the same name
  // can theoretically exist twice across history if someone renames in place.
  @observable categoryFilter: CategoryFilter = 'all';

  // Pinned (server-backed) and recent (localStorage) — drive the stacked
  // sections at the top of the Agents list. Their rows are read from the
  // directory by id, not found in the page the list happens to show.
  @observable pinnedAgentIds = new Set<number>();
  @observable pinnedAgents: AgentDefinition[] = [];
  @observable isLoadingPins = false;
  @observable recentAgentIds: number[] = [];
  @observable private recentRows: AgentDefinition[] = [];

  /** Bumps on every fetch and practice change; a list answered for an older one is dropped (errors too). */
  private fetchSeq = 0;
  /** The same for the pinned and recent rows. */
  private shelfSeq = 0;

  constructor(private root: RootStore) {
    makeObservable(this);

    // Refetch whenever the selected merchant changes, including initial selection.
    reaction(
      () => this.root.merchantContextStore.selectedMerchantId,
      (merchantId) => {
        // The previous practice's roster is not this one's: clear it now rather than
        // show it under the new practice's name until the new list arrives.
        runInAction(() => {
          this.fetchSeq++;
          this.shelfSeq++;
          this.agents = [];
          this.totalCount = 0;
          this.page = 1;
          this.error = null;
          this.isLoading = false;
          this.pinnedAgents = [];
          this.recentRows = [];
        });
        if (merchantId != null) {
          void this.fetch();
          // Pinned agents are per-user (not per-merchant) but a fresh load on
          // merchant switch is cheap and keeps the Pinned section in sync if
          // the user pinned something on another tab.
          void this.fetchPins();
          // Recents are per-merchant (we don't bleed history across tenants).
          runInAction(() => {
            this.recentAgentIds = readRecent(merchantId);
          });
          void this.resolveRecent();
        } else {
          runInAction(() => {
            this.agents = [];
            this.totalCount = 0;
            this.pinnedAgentIds = new Set();
            this.recentAgentIds = [];
          });
        }
      },
      { fireImmediately: true }
    );
  }

  // ── Pinned agents (server-backed) ────────────────────────────────────────

  @action
  async fetchPins(): Promise<void> {
    this.isLoadingPins = true;
    try {
      const rows = await getMyPinnedAgents();
      runInAction(() => {
        this.pinnedAgentIds = new Set(rows.map((r) => r.agentId));
        this.isLoadingPins = false;
      });
      await this.resolvePinned();
    } catch {
      // Soft-fail — Pinned section just shows empty state.
      runInAction(() => { this.isLoadingPins = false; });
    }
  }

  /** The Pinned and Recently opened sections, read again (the list page asks on every visit). */
  async fetchShelves(): Promise<void> {
    await Promise.all([this.fetchPins(), this.resolveRecent()]);
  }

  /** The rows for these ids, in this practice or shared, in the order given; [] for none, and for a late answer. */
  private async rowsFor(ids: number[]): Promise<AgentDefinition[] | null> {
    const merchantId = this.root.merchantContextStore.selectedMerchantId;
    if (merchantId == null || ids.length === 0) return [];
    const seq = this.shelfSeq;
    const rows = await getAgentsByIds(merchantId, ids);
    if (seq !== this.shelfSeq || merchantId !== this.root.merchantContextStore.selectedMerchantId) return null;
    const byId = new Map(rows.map((a) => [a.id, a]));
    return ids.map((id) => byId.get(id)).filter((a): a is AgentDefinition => a != null);
  }

  private async resolvePinned(): Promise<void> {
    try {
      const rows = await this.rowsFor([...this.pinnedAgentIds]);
      if (rows) runInAction(() => { this.pinnedAgents = rows; });
    } catch {
      /* soft-fail — the Pinned section keeps what it had */
    }
  }

  private async resolveRecent(): Promise<void> {
    try {
      const rows = await this.rowsFor(this.recentAgentIds);
      if (rows) runInAction(() => { this.recentRows = rows; });
    } catch {
      /* soft-fail — the Recent section keeps what it had */
    }
  }

  @action
  async togglePin(agentId: number): Promise<void> {
    const isPinned = this.pinnedAgentIds.has(agentId);
    // Optimistic flip — server is idempotent so retries are safe.
    runInAction(() => {
      const next = new Set(this.pinnedAgentIds);
      if (isPinned) next.delete(agentId);
      else next.add(agentId);
      this.pinnedAgentIds = next;
    });
    try {
      if (isPinned) await unpinAgent(agentId);
      else await pinAgent(agentId);
      if (isPinned) runInAction(() => { this.pinnedAgents = this.pinnedAgents.filter((a) => a.id !== agentId); });
      else await this.resolvePinned();
    } catch (err) {
      // Revert on failure.
      runInAction(() => {
        const next = new Set(this.pinnedAgentIds);
        if (isPinned) next.add(agentId);
        else next.delete(agentId);
        this.pinnedAgentIds = next;
      });
      throw err;
    }
  }

  // ── Recent agents (localStorage, per-merchant) ───────────────────────────

  /**
   * Push an agent to the top of the per-merchant recent list. Called from
   * AgentDetailPage on load (and from cmd+K when the user jumps via the
   * global search). Capped at RECENT_MAX entries; oldest evicted.
   */
  @action
  trackOpened(agentId: number): void {
    const merchantId = this.root.merchantContextStore.selectedMerchantId;
    if (merchantId == null) return;
    const existing = this.recentAgentIds.filter((id) => id !== agentId);
    const next = [agentId, ...existing].slice(0, RECENT_MAX);
    this.recentAgentIds = next;
    writeRecent(merchantId, next);
  }

  /**
   * Recent agents, read from the directory by id, in the order opened. Pinned agents are
   * filtered out so the same row never appears in both sections — the user
   * sees the agent once, in the more important place (Pinned).
   */
  @computed get recentAgents(): AgentDefinition[] {
    if (this.recentAgentIds.length === 0) return [];
    const byId = new Map(this.recentRows.map((a) => [a.id, a]));
    return this.recentAgentIds
      .map((id) => byId.get(id))
      .filter((a): a is AgentDefinition => a != null && !this.pinnedAgentIds.has(a.id));
  }

  /** What the list asks the server for: the practice, the text, every filter, the order and the page. */
  @computed get request(): AgentSearchParams | null {
    const merchantId = this.root.merchantContextStore.selectedMerchantId;
    if (merchantId == null) return null;
    return {
      merchantId,
      search: this.search.trim() || undefined,
      includeGlobal: this.includeGlobal,
      audience: this.audienceFilter === 'patient' ? 'patient' : this.audienceFilter === 'careTeam' ? 'care' : undefined,
      status: this.statusFilter === 'all' ? undefined : this.statusFilter,
      router: this.routerFilter === 'enabled' ? 'on' : this.routerFilter === 'disabled' ? 'off' : undefined,
      categoryId: this.categoryFilter === 'all' ? undefined : this.categoryFilter,
      sort: this.sort?.field,
      descending: this.sort?.descending || undefined,
      pageNo: this.page,
      pageSize: this.pageSize,
    };
  }

  @action
  async fetch(): Promise<void> {
    const request = this.request;
    if (request == null) return;
    const merchantId = request.merchantId;

    const seq = ++this.fetchSeq;
    this.isLoading = true;
    this.error = null;
    // Late: a newer fetch started, or the practice changed while this one was out.
    const late = () => seq !== this.fetchSeq || merchantId !== this.root.merchantContextStore.selectedMerchantId;
    try {
      const result = await searchAgents(request);
      runInAction(() => {
        if (late()) return;
        this.agents = result.data ?? [];
        this.totalCount = result.paging?.totalRecordCount ?? this.agents.length;
        this.shownSearch = request.search ?? '';
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        if (late()) return;
        this.isLoading = false;
        this.error = err instanceof Error ? err.message : 'Failed to load agents';
      });
    }
  }

  @action
  setSearch(value: string): void {
    this.search = value;
  }

  /** Asks for the text as typed, from the first page. */
  @action
  submitSearch(): Promise<void> {
    this.page = 1;
    return this.fetch();
  }

  @action
  setIncludeGlobal(value: boolean): void {
    this.includeGlobal = value;
    this.page = 1;
  }

  @action
  setPage(page: number, pageSize: number): Promise<void> {
    // A new page size starts again from the first page, as the table does.
    this.page = pageSize !== this.pageSize ? 1 : page;
    this.pageSize = pageSize;
    return this.fetch();
  }

  @action
  setSort(sort: AgentListSort): Promise<void> {
    this.sort = sort;
    this.page = 1;
    return this.fetch();
  }

  // ── Filters: each change asks the server again, from the first page ─────────

  @action setAudienceFilter(v: AudienceFilter): void { this.audienceFilter = v; void this.refilter(); }
  @action setStatusFilter(v: StatusFilter): void { this.statusFilter = v; void this.refilter(); }
  @action setRouterFilter(v: RouterFilter): void { this.routerFilter = v; void this.refilter(); }
  @action setCategoryFilter(v: CategoryFilter): void { this.categoryFilter = v; void this.refilter(); }

  @action
  clearFilters(): void {
    this.audienceFilter = 'all';
    this.statusFilter = 'all';
    this.routerFilter = 'all';
    this.categoryFilter = 'all';
    void this.refilter();
  }

  @action
  private refilter(): Promise<void> {
    this.page = 1;
    return this.fetch();
  }

  /** Count of non-default filters currently active. Drives the badge on the Filter button. */
  @computed get activeFilterCount(): number {
    let n = 0;
    if (this.audienceFilter !== 'all') n++;
    if (this.statusFilter !== 'all') n++;
    if (this.routerFilter !== 'all') n++;
    if (this.categoryFilter !== 'all') n++;
    return n;
  }

}
